import DividendLogo from "/public/images/logos/Dividend-logo.png";
import GoodleapLogo from "/public/images/logos/Goodleap-logo.png";
import PowerPayLogo from "/public/images/logos/Powerpay-logo.png";
import SunlightLogo from "/public/images/logos/Sunlight-logo.png";
import SunnovaLogo from "/public/images/logos/Sunnova-logo.png";

import { IntegrationFeature, IntegratorItem } from "components/ranked-table/types";

export enum Lenders {
  GOODLEAP = "Goodleap",
  SUNLIGHT = "Sunlight",
  DIVIDEND = "Dividend",
  SUNNOVA = "Sunnova",
  POWERPAY = "PowerPay",
}

const lenders: { [key: string]: IntegratorItem } = {
  [Lenders.SUNLIGHT]: { _id: Lenders.SUNLIGHT, name: Lenders.SUNLIGHT, logo: SunlightLogo, orderRank: "1" },
  [Lenders.GOODLEAP]: { _id: Lenders.GOODLEAP, name: Lenders.GOODLEAP, logo: GoodleapLogo, orderRank: "2" },
  [Lenders.DIVIDEND]: { _id: Lenders.DIVIDEND, name: Lenders.DIVIDEND, logo: DividendLogo, orderRank: "3" },
  [Lenders.POWERPAY]: { _id: Lenders.POWERPAY, name: Lenders.POWERPAY, logo: PowerPayLogo, orderRank: "4" },
  [Lenders.SUNNOVA]: { _id: Lenders.SUNNOVA, name: Lenders.SUNNOVA, logo: SunnovaLogo, orderRank: "5" },
};

export const defaultLendersList = Object.values(lenders).sort((a, b) => Number(a.orderRank) - Number(b.orderRank));

export const defaultLendingFeatures: IntegrationFeature[] = [
  {
    title: "Instant pricing and accurate quotes",
    description:
      "Enerflo can provide accurate quotes for fully integrated lenders, as well as for most non-integrated lenders, based on partner, product and project cost criteria.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
      { item: { _ref: lenders[Lenders.SUNNOVA]._id } },
    ],
  },
  {
    title: "Compliant proposals and disclaimers",
    description:
      "Enerflo dynamically incorporates lender disclaimers into the proposal and application, ensuring compliance.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
      { item: { _ref: lenders[Lenders.SUNNOVA]._id } },
    ],
  },
  {
    title: "Pre-fill application details",
    description:
      "Enerflo is able to pre-fill required data points based on information already entered or known, saving time and repetitive manual entry.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
      { item: { _ref: lenders[Lenders.SUNNOVA]._id } },
    ],
  },
  {
    title: "Embed loan application",
    description:
      "The entire loan application is embedded within Enerflo, eliminating the need for multiple logins to lender portals.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
      { item: { _ref: lenders[Lenders.SUNNOVA]._id } },
    ],
  },
  {
    title: "Email loan application",
    description: "Loan applications can be quickly and easily emailed to the customer applicant from within Enerflo.",
    integrators: [{ item: { _ref: lenders[Lenders.GOODLEAP]._id } }, { item: { _ref: lenders[Lenders.SUNLIGHT]._id } }],
  },
  {
    title: "Embed loan docs for signature",
    description:
      "Loan documents are all embedded within the Enerflo platform, saving time and creating a more streamlined experience for all involved parties.",
    integrators: [
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.SUNNOVA]._id } },
    ],
  },
  {
    title: "Email loan docs for signature",
    description: "Loan docs can be emailed for signature, with status tracking, all from within Enerflo.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
      { item: { _ref: lenders[Lenders.SUNNOVA]._id } },
    ],
  },
  {
    title: "Spanish translation for docs",
    description: "Loan docs can be provided in Spanish for participating lenders.",
    integrators: [{ item: { _ref: lenders[Lenders.GOODLEAP]._id } }, { item: { _ref: lenders[Lenders.SUNLIGHT]._id } }],
  },
  {
    title: "Loan stip visibility",
    description:
      "Enerflo provides stipulation updates in real time to keep the loan application process moving forward as quickly as possible.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
    ],
  },
  {
    title: "Automatically send system details to lender",
    description: "Enerflo automatically updates the application with system equipment and project details.",
    integrators: [{ item: { _ref: lenders[Lenders.DIVIDEND]._id } }, { item: { _ref: lenders[Lenders.SUNNOVA]._id } }],
  },
  {
    title: "Automatically upload signed install agreement",
    description: "Enerflo automatically uploads signed install agreements to the application.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
    ],
  },
  {
    title: "Application status tracking",
    description: "Enerflo can get updates on application status to assist with reporting and project submission.",
    integrators: [
      { item: { _ref: lenders[Lenders.GOODLEAP]._id } },
      { item: { _ref: lenders[Lenders.SUNLIGHT]._id } },
      { item: { _ref: lenders[Lenders.DIVIDEND]._id } },
      { item: { _ref: lenders[Lenders.POWERPAY]._id } },
      { item: { _ref: lenders[Lenders.SUNNOVA]._id } },
    ],
  },
  {
    title: "Integrated change order support",
    description:
      "Enerflo automatically integrates change orders and other project-related updates with the application.",
    integrators: [{ item: { _ref: lenders[Lenders.GOODLEAP]._id } }, { item: { _ref: lenders[Lenders.SUNLIGHT]._id } }],
  },
];
